import React, { useEffect, useState, useContext } from 'react';
import { Card, Col, Row, Statistic, Table, Progress, Spin, Typography } from 'antd';
import { PieChartOutlined, TeamOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { AuthContext } from '../../config/AuthContext';
import { GetDashboardStats } from '../../dao/metadata/GetDashboardStats';

const { Title } = Typography;

const ESTADOS = [
    { id: 0, label: 'Entrevista', color: 'blue' },
    { id: 1, label: 'Pendiante Acuerdo', color: 'cyan' },
    { id: 2, label: 'Firma Alumno', color: 'orange' },
    { id: 3, label: 'Firma Empresa', color: 'geekblue' },
    { id: 4, label: 'Firma Centro', color: 'purple' },
    { id: 5, label: 'Entrega Resp.', color: 'magenta' },
    { id: 6, label: 'En curso', color: 'green' },
    { id: 7, label: 'Finalizada', color: 'gold' },
];

const MetadataDashboard = () => {
    const { userId } = useContext(AuthContext);
    const [stats, setStats] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (userId) {
            fetchStats();
        }
    }, [userId]);

    const fetchStats = async () => {
        setLoading(true);
        const data = await GetDashboardStats(userId);
        setStats(data || []);
        setLoading(false);
    };

    // Process Data
    const statusStats = ESTADOS.map(st => {
        const found = stats.find(s => s.type === 'status' && s.label === st.id.toString());
        return {
            ...st,
            count: found ? parseInt(found.count_val) : 0
        };
    });

    const aulaStats = stats.filter(s => s.type === 'aula').map(s => {
        const total = parseInt(s.count_val);
        const placed = parseInt(s.placed_val);
        const finished = parseInt(s.finished_val);
        const notPlaced = total - placed - finished;
        // Use server-side calculated progress (time-based average)
        const percent = s.progress_val ? Math.round(parseFloat(s.progress_val)) : 0;

        return {
            aula: s.label,
            total,
            placed,
            finished,
            notPlaced,
            percent
        };
    });

    const totalStudents = aulaStats.reduce((acc, curr) => acc + curr.total, 0);
    const totalPlaced = aulaStats.reduce((acc, curr) => acc + curr.placed, 0);
    const totalFinished = aulaStats.reduce((acc, curr) => acc + curr.finished, 0);
    const totalNotPlaced = totalStudents - totalPlaced - totalFinished;

    const columns = [
        { title: 'Aula', dataIndex: 'aula', key: 'aula', render: (text: string) => <b>{text}</b> },
        { title: 'Total Alumnos', dataIndex: 'total', key: 'total', align: 'center' as const },
        { title: 'En Curso (Colocados)', dataIndex: 'placed', key: 'placed', align: 'center' as const, render: (val: number) => <span style={{ color: 'green', fontWeight: 'bold' }}>{val}</span> },
        { title: 'Finalizadas', dataIndex: 'finished', key: 'finished', align: 'center' as const, render: (val: number) => <span style={{ color: 'gold', fontWeight: 'bold' }}>{val}</span> },
        { title: 'No Colocados', dataIndex: 'notPlaced', key: 'notPlaced', align: 'center' as const, render: (val: number) => <span style={{ color: 'red' }}>{val}</span> },
        {
            title: '% Progreso',
            dataIndex: 'percent',
            key: 'percent',
            render: (percent: number) => <Progress percent={percent} size="small" status={percent === 100 ? "success" : "active"} />
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={4}>Resumen General</Title>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Alumnos"
                            value={totalStudents}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="En Curso"
                            value={totalPlaced}
                            prefix={<CheckCircleOutlined style={{ color: 'green' }} />}
                            valueStyle={{ color: 'green' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Finalizadas"
                            value={totalFinished}
                            prefix={<CheckCircleOutlined style={{ color: 'gold' }} />}
                            valueStyle={{ color: 'gold' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="No Colocados"
                            value={totalNotPlaced}
                            prefix={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                            valueStyle={{ color: 'red' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Title level={4}>Estado de Aulas</Title>
            <Table
                columns={columns}
                dataSource={aulaStats}
                rowKey="aula"
                pagination={false}
                loading={loading}
                bordered
                style={{ marginBottom: 24 }}
            />

            <Title level={4}>Estado de Asignaciones</Title>
            <Row gutter={[16, 16]}>
                {statusStats.map(st => (
                    <Col xs={24} sm={12} md={6} lg={4} key={st.id}>
                        <Card
                            size="small"
                            style={{ borderTop: `3px solid ${st.color}` }}
                            loading={loading}
                        >
                            <Statistic
                                title={st.label}
                                value={st.count}
                                valueStyle={{ color: st.color === 'gold' ? '#faad14' : st.color }}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default MetadataDashboard;
